import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import * as MenuPrimitive from '@radix-ui/react-dropdown-menu'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { Icon, Menu, T, Tooltip } from 'govwell-ui'
import { IconKey } from 'govwell-ui/components/Icon/util'
import { getLinkProps, isInternalLink, LinkActionProps } from 'govwell-ui/components/Link/util'
import { getModalRoot } from 'govwell-ui/components/Portal/Portal'
import { isNil } from 'lodash'

import { Link } from '@redwoodjs/router'

import { Badge } from 'src/components/shared/Badge'
import { useSidebarContext } from 'src/components/Sidebar/SidebarPrimitive'
import useDisclosure from 'src/hooks/use-disclosure'
import { useMergeRefs } from 'src/hooks/use-merge-refs'
import { KeyboardKey } from 'src/types'
import { LayoutMode } from 'src/utils/layout'

// Find all scrollable parents
const getScrollableParents = (element: HTMLElement): HTMLElement[] => {
  const parents: HTMLElement[] = []
  let parent = element.parentElement

  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent)
    const isScrollable = [style.overflow, style.overflowY, style.overflowX].some(
      (value) => value === 'auto' || value === 'scroll'
    )

    if (isScrollable) {
      parents.push(parent)
    }
    parent = parent.parentElement
  }

  return parents
}

export type NavElement = NavGroup | NavItem
interface INavItem {
  badge?: number
  ref?: React.RefObject<HTMLElement>
  title: string
}
export type NavGroup = INavItem & {
  icon: IconKey
  items: NavItem[]
  key: string
}
export enum NavGroupKey {
  AIAssistant = 'AIAssistant',
  AutoCheck = 'AutoCheck',
  CodeEnforcement = 'CodeEnforcement',
  Companies = 'Companies',
  Insights = 'Insights',
  Locations = 'Locations',
  Payments = 'Payments',
  RecordType = 'RecordType',
  StaffAIAssistant = 'StaffAIAssistant',
}

export type NavItem = INavItem &
  LinkActionProps & {
    icon?: IconKey
    isActive: boolean
    isHighlighted?: boolean
  }

type Props = {
  elements: NavElement[]
  isNavGroupOpenMap?: Map<string, boolean>
  onToggleIsOpen?: (key: string) => void
}
const Navigation = ({ elements, isNavGroupOpenMap, onToggleIsOpen }: Props) => {
  const { layoutMode } = useSidebarContext()
  const isCollapsed = layoutMode === LayoutMode.SidebarCollapsed
  return elements.map((element) => {
    if ('items' in element) {
      const { key, ...rest } = element
      return (
        <NavGroup
          key={key}
          groupKey={key}
          isCollapsed={isCollapsed}
          isOpen={isNavGroupOpenMap?.get(key) ?? false}
          onToggleIsOpen={onToggleIsOpen}
          {...rest}
        />
      )
    }

    return (
      <Tooltip key={element.title} content={isCollapsed ? element.title : undefined} side="right">
        <div className="relative w-full min-w-fit">
          <NavItemInternal key={element.title} isCollapsed={isCollapsed} {...element} />
        </div>
      </Tooltip>
    )
  })
}

type NavGroupProps = NavGroup & {
  groupKey: string
  isCollapsed: boolean
  isOpen: boolean
  onToggleIsOpen?: (key: string) => void
}
const NavGroup = forwardRef<HTMLElement, NavGroupProps>(
  (
    { badge, groupKey, icon, isCollapsed, isOpen, items, onToggleIsOpen, title }: NavGroupProps,
    ref
  ) => {
    const internalRef = useRef<HTMLElement>(null)
    const menuTriggerRef = useRef<HTMLElement>(null)
    // The trigger ref is shared between the forwarded ref, the internal NavGroup ref, and the
    // hover/scroll/floating-bar machinery (`menuTriggerRef`). Putting all of these on the
    // <button> directly is required for the structural a11y fix: when Menu.Trigger asChild
    // forwards aria-haspopup / aria-expanded / data-state, the IMMEDIATE child must be the
    // <button> itself so those attributes land on a real interactive element. A wrapping
    // <span className="contents"> would intercept those props.
    const mergedRef = useMergeRefs(internalRef, menuTriggerRef, ...(ref ? [ref] : []))
    const handleClick = useCallback(
      (e?: React.MouseEvent | React.KeyboardEvent) => {
        e?.preventDefault()
        onToggleIsOpen?.(groupKey)
      },
      [groupKey, onToggleIsOpen]
    )

    const { isOpen: isMenuOpen, close: closeMenu, open: openMenu } = useDisclosure()

    // Drives the controlled focus tooltip in collapsed mode. When the trigger button receives
    // keyboard focus we show a Radix tooltip with the group title; on blur it goes away. We
    // only render the tooltip when the menu popover isn't already open (otherwise both would
    // stack and Menu.Label inside the popover already shows the title).
    const [isFocused, setIsFocused] = useState(false)
    const handleFocus = useCallback(() => setIsFocused(true), [])
    const handleBlur = useCallback(() => setIsFocused(false), [])

    // When the new <button> trigger is in play, NavItemInternal renders only the visual content
    // (icon + label + chevron) — no nested interactive control. We pass isOpen so the chevron
    // can flip without depending on the [open] attribute of the old <details> wrapper.
    const renderedItem = useMemo(
      () => (
        <NavItemInternal
          badge={isOpen ? undefined : badge}
          icon={icon}
          isCollapsed={isCollapsed}
          isHeader
          isOpen={isOpen}
          title={title}
        />
      ),
      [badge, icon, isCollapsed, isOpen, title]
    )

    const handleMouseEnter = useCallback(() => {
      if (isCollapsed) {
        openMenu()
      }
    }, [isCollapsed, openMenu])

    const menuContentRef = useRef<HTMLDivElement>(null)
    const floatingBarRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      // Forward scroll events from the floating bar to the nav
      const handleFloatingBarWheel = (e: WheelEvent) => {
        e.stopPropagation()
        e.preventDefault()
        if (!menuTriggerRef.current) {
          return
        }
        const scrollableParents = getScrollableParents(menuTriggerRef.current)
        if (scrollableParents[0]) {
          scrollableParents[0].scrollTop += e.deltaY
        }
      }

      if (!isMenuOpen) {
        return
      }

      const frame = requestAnimationFrame(() => {
        // Need to wait a tick because the floating bar mounts one tick after the menu is opened
        const floatingBar = floatingBarRef.current
        if (floatingBar) {
          document.addEventListener('wheel', handleFloatingBarWheel)
        }
      })
      return () => {
        cancelAnimationFrame(frame)
        document.removeEventListener('wheel', handleFloatingBarWheel)
      }
    }, [isMenuOpen])

    useEffect(() => {
      // Guarantee that the menu closes when the cursor exits the menu or floating bar or trigger boundaries
      if (!isCollapsed) {
        return
      }

      let lastMouseX = 0
      let lastMouseY = 0

      const closeMenuIfMouseOutside = () => {
        const isCursorInsideRect = (rect: DOMRect) => {
          return (
            lastMouseX >= rect.left &&
            lastMouseX <= rect.right &&
            lastMouseY >= rect.top &&
            lastMouseY <= rect.bottom
          )
        }

        const menuTriggerRect = menuTriggerRef.current?.getBoundingClientRect()
        const floatingBarRect = floatingBarRef.current?.getBoundingClientRect()
        const menuContentRect = menuContentRef.current?.getBoundingClientRect()

        const isCursorInsideMenu =
          (menuTriggerRect && isCursorInsideRect(menuTriggerRect)) ||
          (floatingBarRect && isCursorInsideRect(floatingBarRect)) ||
          (menuContentRect && isCursorInsideRect(menuContentRect))

        if (!isCursorInsideMenu) {
          closeMenu()
        }
      }

      const handleMenuTriggerMouseOver = (e: MouseEvent) => {
        lastMouseX = e.clientX
        lastMouseY = e.clientY
      }

      const handleWindowMouseMove = (e: MouseEvent) => {
        lastMouseX = e.clientX
        lastMouseY = e.clientY

        closeMenuIfMouseOutside()
      }

      // Handle cases where mouse leaves the entire document
      const handleDocumentMouseLeave = () => {
        closeMenu()
      }

      const handleScroll = () => {
        closeMenuIfMouseOutside()
      }

      const menuTrigger = menuTriggerRef.current
      let scrollableParents: HTMLElement[] = []
      if (menuTrigger) {
        menuTrigger.addEventListener('mouseover', handleMenuTriggerMouseOver)

        scrollableParents = getScrollableParents(menuTrigger)
        scrollableParents.forEach((parent) => {
          parent.addEventListener('scroll', handleScroll)
        })
      }
      window.addEventListener('mousemove', handleWindowMouseMove)
      document.addEventListener('mouseleave', handleDocumentMouseLeave)

      return () => {
        if (menuTrigger) {
          menuTrigger.removeEventListener('mouseover', handleMenuTriggerMouseOver)
        }
        scrollableParents.forEach((parent) => parent.removeEventListener('scroll', handleScroll))
        document.removeEventListener('mouseleave', handleDocumentMouseLeave)
        window.removeEventListener('mousemove', handleWindowMouseMove)
      }
    }, [closeMenu, isCollapsed, openMenu])

    // When a collapsed nav group is hovered, a bar containing the icon shows up to visually connect to the menu, which is just a rectangle
    const [floatingBarTopPx, setFloatingBarTopPx] = useState<number>()
    const [floatingBarLeftPx, setFloatingBarLeftPx] = useState<number>()

    // Calculate how the floating bar should be positioned to conceal the underlying nav icon
    const updateFloatingBarTopPx = useCallback(() => {
      if (!menuTriggerRef.current || !menuContentRef.current) {
        return
      }
      setFloatingBarTopPx(
        menuTriggerRef.current.getBoundingClientRect().top -
          menuContentRef.current.getBoundingClientRect().top
      )
      setFloatingBarLeftPx(
        menuTriggerRef.current.getBoundingClientRect().left -
          menuContentRef.current.getBoundingClientRect().left -
          2
      )
    }, [menuTriggerRef, menuContentRef])

    // When the screen resizes, recalculate the floating bar position
    useEffect(() => {
      const resizeObserver = new ResizeObserver(() => setTimeout(updateFloatingBarTopPx, 500))
      const body = document.documentElement
      if (body) {
        resizeObserver.observe(body)
      }
      return () => {
        resizeObserver.disconnect()
      }
    }, [updateFloatingBarTopPx])

    // When the menu is opened, recalculate the floating bar position
    useEffect(() => {
      if (!isMenuOpen) {
        setFloatingBarTopPx(undefined)
        return
      }
      requestAnimationFrame(() => {
        updateFloatingBarTopPx()
      })
    }, [isMenuOpen, updateFloatingBarTopPx])

    // When the nav group is expanded, close any open menus
    useEffect(() => {
      if (!isCollapsed) {
        closeMenu()
      }
    }, [isCollapsed, closeMenu])

    // The visual "expanded items" panel is shown only when the sidebar is in expanded mode AND
    // the group is open. In collapsed mode, items live inside the Radix Menu popover instead,
    // never in the inline panel.
    const isItemsPanelOpen = isOpen && !isCollapsed

    // The trigger element is always a real <button>. In expanded mode there is no Menu wrapper
    // at all; in collapsed mode the <button> is the SAME final DOM element of two nested Radix
    // Slot-aware asChild wrappers (see the collapsed-mode return below). This is the
    // structural fix for:
    // - `aria-allowed-attr` — Radix `asChild` merges props (aria-haspopup="menu", data-state,
    //   aria-expanded, click handlers) onto its immediate child via React.cloneElement. Slot
    //   only forwards one level deep, so any non-Slot wrapper (e.g. a <span className="contents">
    //   or our own Tooltip/Menu.Trigger wrappers) would absorb those attributes onto a
    //   non-button role and re-introduce the violation. Keeping <button> as the direct Slot
    //   target guarantees the ARIA lands on a role that allows it.
    // - `nested-interactive` — the previous `<details><summary>` + inner-<button> pattern
    //   stacked two interactive elements; collapsing to one <button> eliminates that.
    //
    // We render two distinct trees instead of toggling Menu.Trigger via `isDisabled`, because
    // Menu.Trigger forwards its `disabled` prop onto the cloned <button>; a disabled HTML
    // button does not fire onClick, which would break the expanded-mode toggle behavior.
    // Rendering the bare button in expanded mode keeps the click handler live and also avoids
    // emitting the misleading aria-haspopup="menu" attribute when no popover will ever open.
    //
    // Open/close state for the inline items panel comes from React state (`isOpen`) and is
    // surfaced via `aria-expanded`. In collapsed mode `aria-haspopup` and `aria-expanded` come
    // from Radix (managed by MenuPrimitive.Trigger) and reflect the popover's state.
    // `aria-label={title}` exists alongside the visible focus tooltip: the tooltip is a sighted-
    // keyboard affordance, while `aria-label` is what screen readers announce for the icon-only
    // button (Radix Tooltip content is not exposed to SR by default).
    const triggerButton = (
      <button
        type="button"
        ref={mergedRef as React.Ref<HTMLButtonElement>}
        className={`mb-[1px] block w-full cursor-pointer rounded-lg border-none bg-transparent p-0 text-left focus-visible:outline-1 focus-visible:outline-neutral-950 dark:focus-visible:outline-neutral-50 ${isCollapsed ? 'max-h-[32px] min-h-[32px] max-w-[32px] min-w-[32px]' : 'hover:bg-neutral-150 dark:hover:bg-neutral-600'}`}
        aria-expanded={isCollapsed ? undefined : isOpen}
        aria-label={isCollapsed ? title : undefined}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {renderedItem}
      </button>
    )

    if (!isCollapsed) {
      return (
        <>
          {triggerButton}
          {isItemsPanelOpen && (
            <ol className="ml-4 flex flex-col gap-0.5 border-l-1 border-l-neutral-200 p-0 dark:border-l-neutral-400">
              {items.map((item) => (
                <li key={item.title}>
                  <NavItemInternal {...item} isSubItem />
                </li>
              ))}
            </ol>
          )}
        </>
      )
    }

    // Collapsed mode: chain TooltipPrimitive.Trigger asChild (Slot 1) → MenuPrimitive.Trigger
    // asChild (Slot 2) → <button>. Both Radix primitives use @radix-ui/react-slot, so the two
    // asChild wrappers compose props onto the SAME final <button> via cloneElement. We bypass
    // our own `Menu.Trigger` wrapper here (it is not Slot-aware and would discard the tooltip's
    // props); `Menu.Content` only consumes `MenuContext.toggleIsOpen` (not `triggerRef`) so the
    // popover continues to work, and Radix DropdownMenu's native focus restoration handles
    // return-focus-on-close. The tooltip is fully controlled via `open` so we can gate it on
    // focus + collapsed + menu-closed.
    return (
      <Menu isOpen={isMenuOpen} onClose={closeMenu} onOpen={openMenu} isModal={false}>
        <TooltipPrimitive.Provider delayDuration={100}>
          <TooltipPrimitive.Root open={!isMenuOpen && isFocused}>
            <TooltipPrimitive.Trigger asChild>
              <MenuPrimitive.Trigger asChild>{triggerButton}</MenuPrimitive.Trigger>
            </TooltipPrimitive.Trigger>
            <TooltipPrimitive.Portal container={getModalRoot()}>
              <TooltipPrimitive.Content
                side="right"
                sideOffset={2}
                className="animate-in isolate z-2000 w-fit bg-transparent text-xs text-neutral-950"
              >
                <TooltipPrimitive.Arrow className="size-1.5 w-3 translate-y-[calc(-25%)] rounded-[2px] fill-white stroke-neutral-200 stroke-[2px] [stroke-dasharray:30] [stroke-dashoffset:-33]" />
                <div className="max-w-[min(90vw,_400px)] rounded-md border border-neutral-200 bg-white px-3 py-1.5 shadow-lg">
                  {title}
                </div>
              </TooltipPrimitive.Content>
            </TooltipPrimitive.Portal>
          </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
        <Menu.Content
          side="right"
          className="overflow-visible filter-[drop-shadow(0px_0px_2px_gray)] dark:bg-neutral-800"
          sideOffset={6}
          ref={menuContentRef}
        >
          <div
            className={`fixed -z-1 h-[32px] w-[58px] rounded-md bg-white dark:bg-neutral-800 ${isNil(floatingBarTopPx) ? 'invisible' : ''}`}
            ref={floatingBarRef}
            style={{
              top: floatingBarTopPx,
              left: floatingBarLeftPx,
            }}
          >
            <Icon icon={icon} className="h-[20px] w-[20px] p-2 dark:text-neutral-100" />
          </div>
          <Menu.Label>
            <T.Body className="px-2 py-1 font-semibold dark:text-neutral-100">{title}</T.Body>
          </Menu.Label>
          {items.map((item) => (
            <Menu.Item
              key={item.title}
              className="dark:text-neutral-100 dark:hover:bg-neutral-600 dark:focus:bg-neutral-600 dark:focus:text-neutral-100"
              {...(isInternalLink(item)
                ? { internalRoute: item.internalRoute }
                : item.externalHref
                  ? { externalHref: item.externalHref }
                  : { onClick: item.onClick ?? (() => {}) })}
            >
              {item.title} {item.badge && <Badge count={item.badge} />}
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu>
    )
  }
)

// NavItemInternal supports four shapes:
//   1. Internal link  → renders a <Link to={internalRoute}>
//   2. External link  → renders an <a href={externalHref}>
//   3. Button action  → renders a <button onClick={onClick}>
//   4. NavGroup header (isHeader=true) → renders a non-interactive <span> because the parent
//      NavGroup already renders the <button> trigger. Action props are NOT used in this mode.
//
// Modes 1-3 are validated at the call site via the LinkActionProps discriminated union elsewhere
// in this component. Inside NavItemInternal we accept any combination because the runtime
// branches handle each mode explicitly; the discriminated union still guards external callers.
type NavItemInternalActionProps = {
  externalHref?: string
  internalRoute?: string
  onClick?: (e: React.MouseEvent | React.KeyboardEvent) => void | Promise<void>
}
type NavItemInternalProps = NavItemInternalActionProps & {
  isActive?: boolean
  isCollapsed?: boolean
  isHeader?: boolean
  // Open/closed state of the parent NavGroup; used to flip the chevron icon when isHeader.
  isOpen?: boolean
  isSubItem?: boolean
  isHighlighted?: boolean
  icon?: IconKey
  title: string
  badge?: number
}
const NavItemInternal = forwardRef<HTMLElement, NavItemInternalProps>(
  (props: NavItemInternalProps, ref) => {
    const {
      externalHref,
      icon,
      isActive = false,
      isCollapsed = false,
      isHeader = false,
      isOpen = false,
      isSubItem = false,
      isHighlighted = false,
      onClick,
      title,
      badge,
    } = props
    const mergedRef = useMergeRefs<HTMLElement>(ref)
    const content = useMemo(
      () => (
        <>
          {icon && (
            <Icon
              className={`h-[20px] w-[20px] ${
                isHighlighted
                  ? 'text-orange-700 group-hover:!text-neutral-50 dark:group-hover:!text-neutral-950'
                  : isActive
                    ? 'text-neutral-900 dark:text-neutral-100'
                    : 'text-neutral-700 dark:text-neutral-300'
              } `}
              icon={icon}
            />
          )}
          {!isCollapsed && (
            <>
              <T.Body
                as="span"
                className={`text-left font-medium ${
                  isHighlighted
                    ? 'text-orange-700 group-hover:!text-neutral-50 dark:group-hover:!text-neutral-950'
                    : 'dark:text-neutral-50'
                } ${isHeader ? 'flex-1' : ''}`}
              >
                {title}
              </T.Body>
              {!!badge && <Badge count={badge} />}
              {isHeader && (
                <T.Body className="text-neutral-950 dark:text-neutral-50">
                  {isOpen ? <Icon.FaChevronUp /> : <Icon.FaChevronDown />}
                </T.Body>
              )}
            </>
          )}
        </>
      ),
      [isActive, icon, isCollapsed, title, badge, isHeader, isHighlighted, isOpen]
    )

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === KeyboardKey.Enter || e.key === KeyboardKey.Space) {
          void onClick?.(e)
        }
      },
      [onClick]
    )

    const classNames = `
    relative
    box-border
    flex
    items-center
    gap-3
    px-1.5
    py-2
    w-full
    select-none
    no-underline
    cursor-pointer
    text-neutral-950
    dark:text-neutral-50
    border-none
    ${isHighlighted ? 'group' : ''}
    ${isHighlighted ? '' : 'hover:bg-neutral-150 dark:hover:bg-neutral-600'}
    focus-visible:!outline-1
    focus-visible:!outline-neutral-950
    dark:focus-visible:!outline-neutral-50
    ${isSubItem ? 'rounded-r-lg pl-[21px]' : 'rounded-lg'}
    ${
      isActive
        ? `
      bg-neutral-150
      dark:bg-neutral-600
      ${isSubItem ? 'border-l before:content-[""] before:absolute before:inset-[0px] before:-left-px before:border-l before:border-orange-500' : ''}
    `
        : 'bg-transparent'
    }
    ${
      isHighlighted
        ? 'border-l-2 border-orange-500 bg-orange-50/50 dark:bg-orange-950/30 hover:bg-orange-500 hover:text-neutral-50 dark:hover:bg-orange-500 dark:hover:text-neutral-950'
        : ''
    }
    ${isCollapsed ? 'max-w-[32px] min-w-[32px]' : ''}
  `

    // When used as a NavGroup header (isHeader=true), the parent already renders a real <button>
    // as the trigger. We MUST NOT emit another interactive control here — that's the
    // `nested-interactive` axe failure. A plain <span> carries the visual styling without an
    // ARIA role.
    if (isHeader) {
      return (
        <span className={classNames} ref={mergedRef as React.Ref<HTMLSpanElement>}>
          {content}
        </span>
      )
    }

    if (onClick) {
      return (
        <button
          type="button"
          className={classNames}
          onClick={onClick}
          onKeyDown={handleKeyDown}
          ref={mergedRef}
        >
          {content}
        </button>
      )
    }
    const { rel, target } = getLinkProps(!!externalHref)
    const { internalRoute } = props

    // The header / button / null branches above already short-circuited; here we know one of
    // internalRoute or externalHref is set. Internal route → React Router Link; external href
    // → plain anchor with the external-link icon.
    if (typeof internalRoute === 'string') {
      return (
        <Link className={classNames} ref={mergedRef} rel={rel} target={target} to={internalRoute}>
          {content}
        </Link>
      )
    }
    return (
      <a className={classNames} href={externalHref} ref={mergedRef} rel={rel} target={target}>
        {content} <Icon.FaArrowUpRightFromSquare className="text-sm" />
      </a>
    )
  }
)

export default React.memo(Navigation)
